import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { Button, IconButton, Typography, useMediaQuery } from '@material-ui/core'
import { FolderOpen as FolderOpenIcon } from '@material-ui/icons'
import clsx from 'clsx'
import {
  PaymentMethodDialog,
  PaymentReceiptListDialog,
  UploadReceiptDialog
} from 'pages/Events/ProfilePage/components'
import { ROUTES } from 'routes'
import { EllipseStatus, FileViewerDialog } from 'shared'
import { PAYMENT_TYPES, SALE_ORDER_PAYMENT_STATES, SALE_ORDER_STATES } from 'utils/constants'
import { getActiveInstallment } from 'utils/functions'

import { useStyles } from './InscriptionCard.style'

const observerOptions = {
  Pending: {
    value: <EllipseStatus status='yellow' title='Preinscripto' />,
    label: 'Preinscripto'
  },
  Accepted: {
    value: <EllipseStatus status='green' title='Inscripto' />,
    label: 'Inscripto',
    key: 'Accepted'
  },
  Cancelled: {
    value: <EllipseStatus status='red' title='Anulado' />,
    label: 'Anulado'
  }
}

const InscriptionCard = ({ eventInscription }) => {
  const classes = useStyles()
  const { event } = useSelector((state) => state.events)
  const history = useHistory()
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))

  const { id, category, distance, state, paymentType, saleOrder, ticketTypeId, number } =
    eventInscription ?? {}

  const [openFileViewerDialog, setOpenFileViewerDialog] = useState(false)
  const [openUploadReceiptDialog, setOpenUploadReceiptDialog] = useState(false)
  const [openPaymentMethodDialog, setOpenPaymentMethodDialog] = useState(false)
  const [openPaymentReceiptListDialog, setOpenPaymentReceiptListDialog] = useState(false)

  const ticketType = event.activeInscriptionForm.ticketTypes.find(
    (ticket) => ticket.id === ticketTypeId
  )
  const inscriptionState = observerOptions[state]
  const inscriptionApproved = inscriptionState === observerOptions.Accepted
  const activeInstallment = getActiveInstallment(ticketType)
  const payment = saleOrder?.payments.find(
    (x) =>
      !x.removedAt &&
      (ticketType.hasInstallments ? x.installmentId === activeInstallment?.id : true)
  )

  const hasSaleOrderPending = !inscriptionApproved && (!payment || !payment?.hasReceiptFile)

  const hasPaymentReceipts =
    !inscriptionApproved && saleOrder.payments.some((x) => !x.removedAt && x.hasReceiptFile)

  const inscriptionExpired =
    inscriptionState === observerOptions.Cancelled && saleOrder?.state === SALE_ORDER_STATES.EXPIRED

  const verifyActivePaymentType = (paymentType) => {
    if (!ticketType) return

    switch (paymentType) {
      case PAYMENT_TYPES.MercadoPago:
        return event.mercadoPagoEnabled && ticketType.mercadoPagoEnabled
      case PAYMENT_TYPES.BankAccount:
        return event.eventBankAccountsEnabled && ticketType.bankAccountsEnabled
      case PAYMENT_TYPES.PaymentLocation:
        return event.eventPaymentLocationsEnabled && ticketType.paymentLocationsEnabled
      case PAYMENT_TYPES.PaymentUrl:
        return !!ticketType.paymentUrl
      default:
    }
  }

  const handleRedirectInscriptionForm = () =>
    history.push(`${ROUTES.ROOT}events/${event.slugUrl}/inscription/${id}`)

  const renderViewReceiptFile = () => {
    const { payments } = saleOrder

    const payment = ticketType.hasInstallments
      ? payments.find((p) => p.installmentId === activeInstallment.id && p.hasReceiptFile)
      : payments[0]

    return (
      <>
        <Button
          color='primary'
          variant='contained'
          disabled={!payment.hasReceiptFile}
          className={classes.uploadReceiptButton}
          onClick={() => setOpenFileViewerDialog(true)}>
          Ver comprobante
        </Button>

        {payment.hasReceiptFile && (
          <FileViewerDialog
            title={payment.receiptFile?.fileName}
            fileUrl={payment.receiptFileUrl}
            fileExtension={payment.receiptFile.contentType?.split('/')[1]}
            open={openFileViewerDialog}
            onClose={() => setOpenFileViewerDialog(false)}
          />
        )}
      </>
    )
  }

  return (
    <div className={classes.stateContainer}>
      <div
        className={clsx(classes.stateMainContainer, inscriptionApproved && inscriptionState.key)}>
        {!inscriptionExpired && (
          <>
            <div className={classes.header}>
              <Typography
                color='primary'
                variant='h6'
                className={clsx(
                  classes.title,
                  (!isDesktop || inscriptionApproved) && classes.full
                )}>
                {event.name}
              </Typography>
              {isDesktop && (
                <div className={classes.actionContainer}>
                  {inscriptionState === observerOptions.Pending &&
                    PAYMENT_TYPES[paymentType] !== PAYMENT_TYPES.MercadoPago &&
                    saleOrder &&
                    (ticketType.hasInstallments ? !!activeInstallment : true) &&
                    verifyActivePaymentType(PAYMENT_TYPES[paymentType]) &&
                    (hasSaleOrderPending ? (
                      <>
                        <Button
                          color='primary'
                          variant='contained'
                          title='Subir comprobante'
                          className={classes.uploadReceiptButton}
                          onClick={() => setOpenUploadReceiptDialog(true)}>
                          Subir comprobante
                        </Button>

                        <UploadReceiptDialog
                          open={openUploadReceiptDialog}
                          saleOrder={saleOrder}
                          eventInscription={eventInscription}
                          onClose={() => setOpenUploadReceiptDialog(false)}
                        />
                      </>
                    ) : (
                      renderViewReceiptFile()
                    ))}

                  {inscriptionState === observerOptions.Cancelled && (
                    <Button
                      color='primary'
                      variant='contained'
                      className={classes.retryInscription}
                      onClick={handleRedirectInscriptionForm}>
                      Inscribirme
                    </Button>
                  )}

                  {ticketType.hasInstallments && hasPaymentReceipts && (
                    <>
                      <IconButton
                        color='primary'
                        className={clsx(classes.folderButton)}
                        size='small'
                        onClick={() => setOpenPaymentReceiptListDialog(true)}>
                        <FolderOpenIcon />
                      </IconButton>

                      <PaymentReceiptListDialog
                        open={openPaymentReceiptListDialog}
                        installments={ticketType.installments}
                        saleOrder={saleOrder}
                        onClose={() => setOpenPaymentReceiptListDialog(false)}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
            <Typography className={classes.item}>
              {distance.name} {category.name}
            </Typography>
            <div className={classes.state}>
              <div className={classes.inscriptionState} title={inscriptionState.label}>
                ESTADO {inscriptionState.value}
                {isDesktop && inscriptionState.label}
                {!!paymentType &&
                  observerOptions[state] === observerOptions.Pending &&
                  !hasSaleOrderPending &&
                  payment?.state === SALE_ORDER_PAYMENT_STATES.PENDING && (
                    <Typography
                      className={classes.saleOrderState}
                      color='primary'
                      variant='caption'>
                      (Pendiente revisión)
                    </Typography>
                  )}
              </div>

              {paymentType &&
                inscriptionState === observerOptions.Pending &&
                hasSaleOrderPending &&
                (ticketType.hasInstallments ? !!activeInstallment : true) && (
                  <>
                    <Typography
                      color='primary'
                      variant='h6'
                      className={classes.paymentMethodLink}
                      onClick={() => setOpenPaymentMethodDialog(true)}>
                      {verifyActivePaymentType(PAYMENT_TYPES[paymentType])
                        ? 'Medio de pago elegido'
                        : 'Seleccionar modalidad de pago.'}
                    </Typography>
                    <PaymentMethodDialog
                      open={openPaymentMethodDialog}
                      eventInscription={eventInscription}
                      activePaymentType={verifyActivePaymentType(PAYMENT_TYPES[paymentType])}
                      onClose={() => setOpenPaymentMethodDialog(false)}
                    />
                  </>
                )}
            </div>
            {number?.number && (
              <Typography variant='h6'>Número asignado&nbsp; {number.number}</Typography>
            )}
            {!isDesktop && (
              <div className={classes.actionContainer}>
                {inscriptionState === observerOptions.Pending &&
                  PAYMENT_TYPES[paymentType] !== PAYMENT_TYPES.MercadoPago &&
                  !!saleOrder &&
                  (ticketType.hasInstallments ? !!activeInstallment : true) &&
                  (hasSaleOrderPending ? (
                    <>
                      <Button
                        color='primary'
                        variant='contained'
                        className={classes.uploadReceiptButton}
                        onClick={() => setOpenUploadReceiptDialog(true)}>
                        Subir comprobante
                      </Button>
                      <UploadReceiptDialog
                        open={openUploadReceiptDialog}
                        saleOrder={saleOrder}
                        eventInscription={eventInscription}
                        onClose={() => setOpenUploadReceiptDialog(false)}
                      />
                    </>
                  ) : (
                    renderViewReceiptFile()
                  ))}

                {inscriptionState === observerOptions.Cancelled && (
                  <Button
                    color='primary'
                    variant='contained'
                    className={classes.uploadReceiptButton}
                    onClick={handleRedirectInscriptionForm}>
                    Inscribirme
                  </Button>
                )}

                {ticketType.hasInstallments && hasPaymentReceipts && (
                  <>
                    <IconButton
                      color='primary'
                      className={clsx(classes.folderButton)}
                      size='small'
                      onClick={() => setOpenPaymentReceiptListDialog(true)}>
                      <FolderOpenIcon />
                    </IconButton>

                    <PaymentReceiptListDialog
                      open={openPaymentReceiptListDialog}
                      installments={ticketType.installments}
                      saleOrder={saleOrder}
                      onClose={() => setOpenPaymentReceiptListDialog(false)}
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}

        {inscriptionExpired && (
          <>
            <Typography color='primary' variant='h6'>
              {PAYMENT_TYPES[paymentType] === PAYMENT_TYPES.MercadoPago
                ? `La inscripción al evento ${event.name} se dió de baja automáticamente, por problemas con el pago`
                : `La preinscripción al evento ${event.name} se dió de baja automáticamente, porque el comprobante no fue subido en el plazo establecido por el organizador`}
              .
              <br />
              {ticketType.hasInstallments ? (
                <>
                  Para resolver tu situación comunicate con el organizador, hace click&nbsp;
                  <Link
                    className={classes.link}
                    to={`/organizations/profile/${
                      event.organization.handle || event.organization.id
                    }`}>
                    aquí.
                  </Link>
                </>
              ) : (
                <>
                  Si deseas retomar la inscripción hacé click&nbsp;
                  <Link className={classes.link} to={`/events/${event.slugUrl}/inscription/${id}`}>
                    aquí.
                  </Link>
                </>
              )}
            </Typography>
          </>
        )}
      </div>
    </div>
  )
}

export default InscriptionCard
